import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ProfileContext } from '@/contexts/ProfileContext';
import { useAuth } from '@/hooks/useAuth';
import { createSpouseOnCrm, spouseNeedsCreation, toCreateSpouseRequest } from '@/lib/spouse';
import { z } from 'zod';
import { profileSchema } from '@mr-website/schemas';

const fetchProfile = async (): Promise<z.infer<typeof profileSchema> | undefined> => {
  const { data } = await api.get<z.infer<typeof profileSchema>>('/profile/user');
  if (!data) return data;
  // Normalize date fields from the CRM — it may return full ISO timestamps
  // (e.g. "1990-05-15T00:00:00+00:00") but the schema expects YYYY-MM-DD only.
  if (data.birth_date) {
    data.birth_date = (data.birth_date as string).split('T')[0];
  }
  return data;
};

export function useProfile() {
  const { isAuthenticated } = useAuth();
  const profileSnapshot = useContext(ProfileContext);
  if (!profileSnapshot) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }

  const query = useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
    staleTime: Infinity,
    enabled: isAuthenticated,
  });

  return {
    profile: query.data,
    retry: query.refetch,
    isLoading: query.isLoading,
    isError: query.isError,
    ...profileSnapshot,
  } as const;
}

const putProfile = async (profileData: Partial<z.infer<typeof profileSchema>>): Promise<void> => {
  const payload = { ...profileData };

  if (spouseNeedsCreation(payload.spouse)) {
    await createSpouseOnCrm(toCreateSpouseRequest(payload.spouse));
    delete payload.spouse;
  }

  if (Object.keys(payload).length === 0) {
    return;
  }

  await api.patch<z.infer<typeof profileSchema>>('/profile/user', payload);
};

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putProfile,
    onSuccess: (_, variables) => {
      queryClient.setQueryData<z.infer<typeof profileSchema>>(['profile'], (prev) =>
        prev ? { ...prev, ...variables } : prev,
      );
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
